<template>
  <div class="container-fluid">
    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="loadAll()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido cuando hay datos -->
    <div v-else-if="welcome != null">      
      
      <!-- Tarjetas con los documentos obtenidos del request -->
      <div class="row starter-main">
        <div class="col-sm-12 col-lg-12 mt-3" v-for="post in welcome" :key="post.id">
          <div class="card">
            <img
              v-if="post.thumbnail_rendition"
              :src="post.thumbnail_rendition.url"
              :alt="post.thumbnail_rendition.alt"
              class="card-img-top"
              style="object-fit: contain; max-height: 160px; padding: 1rem;"
            />
            <div class="card-header">
              <h5>{{ post.title }}</h5>
            </div>
            <div class="card-body">
              <!-- Embeber el video de youtube con URL src="https://www.youtube.com/embed/qKNRtg6xpcs?rel=0" -->
              <div class="mb-3" style="max-width: 700px; margin-left: auto; margin-right: auto;">
                <div style="position: relative; width: 100%; padding-top: 56.25%;">
                  <iframe
                    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
                    src="https://drive.google.com/file/d/1tBWWsRFAuo6z6OaHAWcgQCnzw5luwM77/preview"
                    frameborder="0"
                    allow="autoplay"
                    allowfullscreen
                  ></iframe>
                </div>
              </div>
              <div class="post-body" v-html="post.body"></div>
            </div>            
          </div>
        </div>
      </div>



    </div>  <!-- /v-else-if -->
  </div>  <!-- /container-fluid -->
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";

/**
 * Mapa de configuración de contenido por perfil.
 * Agrega aquí los IDs y parámetros de cada perfil.
 * Clave: nombre del grupo tal como viene del API (groups[0])
 */
const PROFILE_CONFIG = {
  AMSDE:        { welcomeId: 22 },
  Gobierno:     { welcomeId: 46 },
  Inversionista: { welcomeId: 68 },
  Tractora:     { welcomeId: 69 },
  MiPyMEs:      { welcomeId: 22 },
};

/** Devuelve la config del perfil activo, o fallback a AMSDE si no está mapeado */
function getProfileConfig() {
  const group = authService.getUserGroup();
  return PROFILE_CONFIG[group] ?? PROFILE_CONFIG['AMSDE'];
}

export default {
  name: "LandingPage",
  data() {
    return {
      welcome: null,
      blogPosts: null,
      error: null,
      loading: true,
    };
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([this.fetchPosts(), this.fetchBlog()]);
      } finally {
        this.loading = false;
      }
    },
    async fetchPosts() {
      try {
        const config = getProfileConfig();
        if (!config.welcomeId) {
          this.welcome = [];
          return;
        }
        const { data } = await api.get(`blog?id=${config.welcomeId}&fields=intro,body,thumbnail,thumbnail_rendition,footer&order=first_published_at`);
        const items = data.items ?? (Array.isArray(data) ? data : [data]);
        this.welcome = await Promise.all(items.map(async (post) => ({
          ...post,
          body: await this.resolveBodyImages(post.body),
        })));
      } catch (err) {
        this.error = err;
        console.error("error en fetchPosts", err);
      }
    },
    async fetchBlog() {
      try {
        const { data } = await api.get("blog?child_of=4&fields=intro,body,thumbnail,thumbnail_rendition,footer&order=first_published_at");
        this.blogPosts = data.items ?? (Array.isArray(data) ? data : [data]);
      } catch (err) {
        this.error = err;
        console.error("error en fetchBlog", err);
      }
    },
    async resolveBodyImages(html) {
      if (!html) return html;
      const BASE = 'https://rverdeadmin.klatus.systems';

      // Collect unique image IDs from <embed embedtype="image" id="X" ...>
      const ids = new Set();
      const re = /<embed[^>]*embedtype="image"[^>]*id="(\d+)"[^>]*/gi;
      let match;
      while ((match = re.exec(html)) !== null) {
        ids.add(match[1]);
      }

      // Fetch all images in parallel (only if there are image embeds)
      const imageMap = {};
      if (ids.size) {
        await Promise.all([...ids].map(async (id) => {
          try {
            const { data } = await api.get(`images/${id}/`);
            imageMap[id] = data;
          } catch (e) {
            console.warn(`No se pudo cargar imagen ID ${id}`, e);
          }
        }));
      }

      // Replace <embed> tags with <img> or <iframe> depending on embedtype
      const result = html.replace(/<embed([^>]*)\/?>(?:<\/embed>)?/gi, (fullMatch, attrs) => {
        // --- Media embed (YouTube / Vimeo) ---
        if (/embedtype="media"/i.test(attrs)) {
          const urlMatch = /\burl="([^"]*)"/i.exec(attrs);
          if (!urlMatch) return fullMatch;
          const raw = urlMatch[1];
          let embedSrc = raw;
          // Convert youtu.be short URL → youtube embed
          const ytShort = /youtu\.be\/([A-Za-z0-9_-]+)/i.exec(raw);
          const ytLong  = /youtube\.com\/watch\?v=([A-Za-z0-9_-]+)/i.exec(raw);
          if (ytShort) embedSrc = `https://www.youtube.com/embed/${ytShort[1]}`;
          else if (ytLong) embedSrc = `https://www.youtube.com/embed/${ytLong[1]}`;
          // Convert vimeo.com/ID → vimeo embed
          const vimeo = /vimeo\.com\/(\d+)/i.exec(raw);
          if (vimeo) embedSrc = `https://player.vimeo.com/video/${vimeo[1]}`;
          // Convert Google Drive share/view URL → preview embed
          const gdrive = /drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/i.exec(raw);
          const gdriveOpen = /drive\.google\.com\/open\?id=([A-Za-z0-9_-]+)/i.exec(raw);
          if (gdrive) embedSrc = `https://drive.google.com/file/d/${gdrive[1]}/preview`;
          else if (gdriveOpen) embedSrc = `https://drive.google.com/file/d/${gdriveOpen[1]}/preview`;
          return `<div class="ratio ratio-16x9 my-3 mx-auto" style="max-width:720px;">` +
                 `<iframe src="${embedSrc}" frameborder="0" allowfullscreen ` +
                 `allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture">` +
                 `</iframe></div>`;
        }

        // --- Image embed ---
        if (!/embedtype="image"/i.test(attrs)) return fullMatch;
        const idMatch  = /\bid="(\d+)"/i.exec(attrs);
        const altMatch = /\balt="([^"]*)"/i.exec(attrs);
        const fmtMatch = /\bformat="([^"]*)"/i.exec(attrs);
        if (!idMatch) return fullMatch;
        const img = imageMap[idMatch[1]];
        if (!img) return '';
        // Build absolute URL: handle relative paths like /media/...
        const rawUrl = img.meta?.download_url || img.url || '';
        const url = rawUrl.startsWith('http') ? rawUrl : `${BASE}${rawUrl}`;
        const alt = altMatch ? altMatch[1] : (img.title || '');
        return `<img src="${url}" alt="${alt}" class="img-fluid d-block mx-auto mb-2" style="max-width: 90%;" />`;
      });
      return this.linkifyHtml(result);
    },
    linkifyHtml(html) {
      if (!html) return html;
      // Replace plain-text URLs (not already inside an href or src attribute) with <a> tags
      return html.replace(
        /(?<!href=["']|src=["'])(https?:\/\/[^\s<>"']+)/gi,
        '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
      );
    },
  },
};
</script>


